import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { INSPECTION_STATUSES, type Bereich } from "@jornado/shared";
import { ApiError, api, bildLaden, dateiLaden, dateiSenden, fehlertext } from "./api";
import { ablageAendern, holenMitAblage } from "./ablage";
import { AblageHinweis } from "./netzstand";
import { beobachten, eintraege, sendenOderEinreihen } from "./warteschlange";
import { beschriftung, zielbereich } from "./baukasten";
import { bereichsName, useBereiche } from "./bereiche";
import { Unterschriftsfeld } from "./unterschrift";
import { objektAuswahl } from "./objektname";
import { Auswahl, Eingabe, Fehler, Karte, Knopf, Laedt, Leer, Seitenkopf, Textfeld } from "./ui";

/**
 * Pruefungen und Berichte in der Oberflaeche.
 *
 * Die Erfassung ist fuer das Telefon gebaut: grosse Schaltflaechen, eine
 * Spalte, jede Eingabe wird sofort gespeichert. Ein Mitarbeiter am Objekt hat
 * weder Zeit noch Netz fuer ein Formular, das man am Ende abschickt.
 */

type I18nText = Record<string, string>;

interface PruefungZeile {
  id: string; number: number; status: string;
  started_at: string | null; submitted_at: string | null;
  property_name: string; customer_name: string; employee_name: string;
  punkte: number; erledigt: number; maengel: number;
}

interface Punkt {
  id: string;
  module_name_snapshot: I18nText | null;
  gruppe_snapshot: I18nText | null;
  label_snapshot: I18nText;
  note_snapshot: I18nText;
  input_type_snapshot: string;
  unit_snapshot: string | null;
  target_min_snapshot: string | null;
  target_max_snapshot: string | null;
  result: string | null;
  value_number: string | null;
  value_text: string | null;
  value_bool: boolean | null;
  comment: string | null;
  comment_lang: string | null;
  is_out_of_range: boolean;
  photo_required_snapshot?: boolean;
  sort_order: number;
}

interface Foto {
  id: string;
  media_id: string;
  thumb_id: string | null;
  item_id: string | null;
  caption: string | null;
}

interface Pruefung {
  id: string; number: number; status: string;
  property_name: string; customer_name: string; employee_name: string;
  customer_language: string;
  summary_lang: string | null; summary_text: string | null;
  review_comment: string | null;
  /** Was nur das Buero sieht. Nie im PDF und damit nie beim Eigentuemer. */
  internal_note: string | null;
  /** Der Tag des Besuchs. Er, nicht der Termin, klammert den Bericht. */
  visit_date: string;
  /** Die Abnahme durch den Kunden. Freiwillig, siehe unterschrift.tsx. */
  signed_name: string | null;
  signature_media_id: string | null;
}

const OFFEN = ["draft", "changes_requested"];

// --------------------------------------------------------------------- Liste

export function PruefungenListe() {
  const { t, i18n } = useTranslation();
  const [liste, setListe] = useState<PruefungZeile[] | null>(null);
  const [status, setStatus] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    const p = new URLSearchParams();
    if (status) p.set("status", status);
    void api.get<{ inspections: PruefungZeile[] }>(`/inspections?${p}`)
      .then((r) => { setListe(r.inspections); setFehler(null); })
      .catch((e) => setFehler(fehlertext(e, t("fehler.laden"))));
  }, [status, t]);

  /*
   * Zwei Abschnitte aus einer Liste. Beides steht bewusst hier und nicht auf
   * dem Server: es ist eine Frage der Darstellung, und der Server liefert
   * ohnehin schon alles, was dafuer noetig ist.
   */
  const zurFreigabe = (liste ?? [])
    .filter((p) => p.status === "submitted")
    .sort((a, b) => (a.submitted_at ?? "").localeCompare(b.submitted_at ?? ""));

  const nachKunde = [...(liste ?? [])
    .filter((p) => p.status !== "submitted")
    .reduce((karte, p) => {
      const bisher = karte.get(p.customer_name) ?? [];
      bisher.push(p);
      karte.set(p.customer_name, bisher);
      return karte;
    }, new Map<string, PruefungZeile[]>())]
    .sort((a, b) => a[0].localeCompare(b[0]));

  return (
    <>
      <Seitenkopf
        titel={t("pruefung.titel")}
        hinweis={liste ? t("pruefung.anzahl", { count: liste.length }) : undefined}
        aktionen={<Link to="/pruefungen/neu"><Knopf>{t("pruefung.neu")}</Knopf></Link>}
      />
      <div className="mb-4 max-w-xs">
        <Auswahl value={status} onChange={(e) => setStatus(e.target.value)} aria-label={t("pruefung.status")}>
          <option value="">{t("pruefung.alleStatus")}</option>
          {INSPECTION_STATUSES.map((s) => (
            <option key={s} value={s}>{t(`pruefung.status.${s}`)}</option>
          ))}
        </Auswahl>
      </div>
      <Fehler text={fehler} />
      {!liste ? <Laedt /> : liste.length === 0 ? <Leer text={t("pruefung.leer")} /> : (
        <>
          {/*
            Was auf eine Entscheidung wartet, steht ueber allem anderen.
            Sortiert nach dem Zeitpunkt der Einreichung, aeltester zuerst: wer
            am laengsten wartet, wartet sonst weiter, weil der neueste Bericht
            oben stuende.
          */}
          {zurFreigabe.length > 0 && (
            <section className="mb-6">
              <h2 className="mb-2 text-sm font-bold uppercase tracking-wide text-verweis">
                {t("pruefung.zurFreigabe", { count: zurFreigabe.length })}
              </h2>
              <ul className="space-y-2">
                {zurFreigabe.map((p) => <Zeile key={p.id} p={p} />)}
              </ul>
            </section>
          )}

          {/*
            Alles Uebrige nach Kunden. Ein Bericht steht immer im
            Zusammenhang mit dem Haus davor und dem danach, und in einer nach
            Datum sortierten Liste liegen die drei Berichte desselben Kunden
            drei Bildschirme auseinander.
          */}
          {nachKunde.map(([kunde, eintraege]) => (
            <section key={kunde} className="mb-6">
              <h2 className="mb-2 text-sm font-bold text-tinte">{kunde}</h2>
              <ul className="space-y-2">
                {eintraege.map((p) => <Zeile key={p.id} p={p} kundeZeigen={false} />)}
              </ul>
            </section>
          ))}
        </>
      )}
    </>
  );
}

/**
 * Eine Zeile der Uebersicht.
 *
 * Unter einer Kundenueberschrift steht der Kundenname nicht noch einmal, dort
 * fuehrt das Objekt. Eine Zeile, die wiederholt, was zwei Zentimeter darueber
 * steht, kostet Platz und sagt nichts.
 */
function Zeile({ p, kundeZeigen = true }: { p: PruefungZeile; kundeZeigen?: boolean }) {
  const { t, i18n } = useTranslation();
  return (
    <li>
      <Link to={`/pruefungen/${p.id}`} className="block rounded-card bg-white p-4 transition hover:bg-marke/10">
        <div className="flex flex-wrap items-center gap-2">
          {kundeZeigen && <span className="font-semibold">{p.customer_name}</span>}
          <span className={kundeZeigen ? "text-sm text-leise" : "font-semibold"}>
            {p.property_name}
          </span>
          <StatusMarke status={p.status} />
          {p.maengel > 0 && (
            <span className="rounded bg-red-100 px-2 py-0.5 text-xs text-red-800">
              {t("pruefung.maengel", { count: p.maengel })}
            </span>
          )}
        </div>
        <div className="mt-1 text-sm text-leise">
          {t("pruefung.nummer", { nummer: p.number })} · {p.employee_name} ·{" "}
          {t("pruefung.fortschritt", { erledigt: p.erledigt, gesamt: p.punkte })}
          {p.started_at ? ` · ${new Date(p.started_at).toLocaleDateString(i18n.language)}` : ""}
        </div>
      </Link>
    </li>
  );
}

function StatusMarke({ status }: { status: string }) {
  const { t } = useTranslation();
  const farbe =
    status === "approved" || status === "sent" ? "bg-green-100 text-green-900"
      : status === "submitted" ? "bg-marke/30"
      : status === "changes_requested" ? "bg-red-100 text-red-800"
      : status === "cancelled" ? "bg-leise/20 text-leise"
      : "bg-grund";
  return <span className={`rounded px-2 py-0.5 text-xs font-semibold ${farbe}`}>{t(`pruefung.status.${status}`)}</span>;
}

// -------------------------------------------------------------------- Starten

export function PruefungNeu() {
  const { t, i18n } = useTranslation();
  const { bereiche: alleBereiche } = useBereiche();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [objekte, setObjekte] = useState<{ id: string; name: string; customer_name: string }[]>([]);
  const [objekt, setObjekt] = useState(params.get("objekt") ?? "");
  const [listen, setListen] = useState<{ id: string; label: string | null; bereiche: Bereich[]; is_active: boolean }[]>([]);
  const [wahl, setWahl] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  useEffect(() => {
    void api.get<{ properties: typeof objekte }>("/properties").then((r) => setObjekte(r.properties)).catch(() => {});
  }, []);
  useEffect(() => {
    setWahl("");
    if (!objekt) { setListen([]); return; }
    void api.get<{ checklists: typeof listen }>(`/properties/${objekt}/checklists`)
      .then((r) => setListen(r.checklists.filter((c) => c.is_active))).catch(() => {});
  }, [objekt]);

  async function starten() {
    setLaeuft(true);
    setFehler(null);
    try {
      const r = await api.post<{ id: string }>("/inspections", {
        propertyChecklistId: wahl,
        // Ein Schluessel je Start. Kommt die Antwort nicht an und der Nutzer
        // tippt erneut, entsteht trotzdem nur ein Bericht.
        clientRef: `web-${wahl}-${Date.now()}`
      });
      navigate(`/pruefungen/${r.id}`);
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
      setLaeuft(false);
    }
  }

  return (
    <>
      <Seitenkopf titel={t("pruefung.neu")} hinweis={t("pruefung.neuHinweis")} />
      <Fehler text={fehler} />
      <Karte>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-sm font-medium">{t("objekte.titel")}</span>
            <Auswahl value={objekt} onChange={(e) => setObjekt(e.target.value)}>
              <option value="">{t("objekte.kundeWaehlen")}</option>
              {objektAuswahl(objekte).map(({ objekt: o, beschriftung: text }) => (
                <option key={o.id} value={o.id}>{text}</option>
              ))}
            </Auswahl>
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium">{t("checkliste.titel")}</span>
            <Auswahl value={wahl} onChange={(e) => setWahl(e.target.value)} disabled={!objekt}>
              <option value="">{t("objekte.kundeWaehlen")}</option>
              {listen.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label || t("checkliste.ohneBezeichnung")}
                  {c.bereiche?.length
                    ? ` · ${c.bereiche.map((b) => bereichsName(alleBereiche, b, i18n.language)).join(", ")}`
                    : ""}
                </option>
              ))}
            </Auswahl>
          </label>
        </div>
        {objekt && listen.length === 0 && (
          <p className="mt-3 text-sm text-leise">{t("pruefung.keineCheckliste")}</p>
        )}
        <div className="mt-6 flex gap-3">
          <Knopf onClick={starten} disabled={!wahl || laeuft}>
            {laeuft ? t("aktion.speichert") : t("pruefung.starten")}
          </Knopf>
          <Knopf art="neben" onClick={() => navigate("/pruefungen")}>{t("aktion.abbrechen")}</Knopf>
        </div>
      </Karte>
    </>
  );
}

// ------------------------------------------------------------------ Erfassen

export function PruefungSeite({ darfFreigeben }: { darfFreigeben: boolean }) {
  const { t, i18n } = useTranslation();
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [pruefung, setPruefung] = useState<Pruefung | null>(null);
  const [punkte, setPunkte] = useState<Punkt[]>([]);
  const [fotos, setFotos] = useState<Foto[]>([]);
  /**
   * Wer sonst noch an diesem Bericht schreibt.
   *
   * Leer bei einem Bericht ohne Termin und bei einer Kopie auf dem Geraet, die
   * das Feld noch nicht kennt. Dann steht die Zeile nicht da, statt dass die
   * Ansicht abstuerzt.
   */
  const [team, setTeam] = useState<{ id: string; name: string }[]>([]);
  /**
   * Wie weit der ganze Bericht ist, nicht nur der eigene Teil.
   *
   * Der Gaertner sieht sieben Punkte und hat sie alle abgehakt. Eingereicht
   * wird trotzdem erst, wenn auch die Poolpunkte des Kollegen stehen. Ohne
   * diese Zahl staende bei ihm "sieben von sieben" und daneben ein Knopf, der
   * nicht geht, und das sieht nach einem Fehler der Anwendung aus.
   */
  const [gesamt, setGesamt] = useState<{ punkte: number; erledigt: number } | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [offen, setOffen] = useState<{ punkt: I18nText; grund: string }[]>([]);
  /** Ein ruhiger Hinweis, kein Fehler: ohne Netz gespeichert. */
  const [hinweis, setHinweis] = useState<string | null>(null);
  /** Gerade freigegeben: die Versandkarte fragt dann von selbst nach. */
  const [nachFreigabe, setNachFreigabe] = useState(false);
  /** Wie oft Claude fuer diesen Bericht noch schreiben darf. */
  const [verbleibend, setVerbleibend] = useState(0);
  /** Gesetzt, wenn der Bericht aus der Kopie auf dem Geraet kommt. */
  const [ablageStand, setAblageStand] = useState<string | null>(null);

  /*
   * Der Bericht kommt vom Server, und wenn es kein Netz gibt, aus der Kopie
   * auf dem Geraet. Genau dafuer ist sie da: am Objekt steht der Mitarbeiter
   * oft ohne Empfang vor der Checkliste, die er ausfuellen soll.
   */
  function laden() {
    void holenMitAblage<{
      inspection: Pruefung; items: Punkt[]; fotos: Foto[];
      team?: { id: string; name: string }[];
      gesamt?: { punkte: number; erledigt: number };
      vorschlaege?: { grenze: number; verbleibend: number };
    }>(`/inspections/${id}`)
      .then(({ daten, stand }) => {
        setPruefung(daten.inspection); setPunkte(daten.items); setFotos(daten.fotos ?? []);
        setTeam(daten.team ?? []);
        setGesamt(daten.gesamt ?? null);
        setVerbleibend(daten.vorschlaege?.verbleibend ?? 0);
        setAblageStand(stand);
      })
      .catch((e) => setFehler(fehlertext(e, t("fehler.laden"))));
  }
  useEffect(laden, [id]);

  if (fehler && !pruefung) return <Fehler text={fehler} />;
  if (!pruefung) return <Laedt />;

  const bearbeitbar = OFFEN.includes(pruefung.status);

  /**
   * Einen Pruefpunkt speichern. Gibt zurueck, ob es geklappt hat.
   *
   * Der Rueckgabewert ist nicht Zierde. Ohne ihn steht der Fehler nur oben auf
   * der Seite, und auf dem Telefon ist "oben" beim zwanzigsten Punkt ausserhalb
   * des Bildschirms. Der Wert bleibt dann im Feld stehen, der Mitarbeiter haelt
   * ihn fuer gespeichert und geht weiter. Genau so ist der pH-Wert dreimal
   * verlorengegangen.
   */
  async function speichern(punkt: Punkt, aenderung: Record<string, unknown>): Promise<boolean> {
    // Sofort in der Anzeige, danach beim Server. Wer am Objekt steht, soll
    // nicht auf das Netz warten, bevor der naechste Punkt dran ist.
    const gemerkt = umbenennen(aenderung);
    setPunkte((alt) => alt.map((p) => (p.id === punkt.id ? { ...p, ...gemerkt } : p)));
    try {
      const art = await sendenOderEinreihen(
        {
          art: "pruefpunkt",
          pfad: `/inspections/${id}/items/${punkt.id}`,
          methode: "PATCH",
          koerper: aenderung,
          beschreibung: beschriftung(punkt.label_snapshot, i18n.language, "—")
        },
        async () => {
          const r = await api.patch<{ ausserhalbZielbereich: boolean }>(
            `/inspections/${id}/items/${punkt.id}`, aenderung
          );
          setPunkte((alt) => alt.map((p) => (
            p.id === punkt.id ? { ...p, is_out_of_range: r.ausserhalbZielbereich } : p
          )));
        }
      );

      if (art === "eingereiht") {
        /*
         * Ohne Netz gespeichert. Die Kopie auf dem Geraet wird mitgefuehrt,
         * sonst stuende nach einem Neuladen am Objekt wieder der alte Stand da
         * und der Mitarbeiter traegt dasselbe ein zweites Mal ein.
         */
        await ablageAendern<{ items: Punkt[] }>(`/inspections/${id}`, (a) => ({
          ...a,
          items: a.items.map((p) => (p.id === punkt.id ? { ...p, ...gemerkt } : p))
        }));
      }
      setFehler(null);
      return true;
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
      laden();
      return false;
    }
  }

  async function einreichen() {
    setFehler(null); setOffen([]);
    try {
      /*
       * Auch die Einreichung darf ohne Netz gehen. Sie steht dann hinter den
       * Pruefpunkten und Fotos in der Schlange, und weil die Schlange ihre
       * Reihenfolge haelt, kommt sie beim Server erst an, wenn alles da ist.
       *
       * 409 gilt dabei als erledigt: der Bericht ist schon eingereicht, der
       * erste Versuch kam also an und nur die Antwort ging verloren.
       */
      const art = await sendenOderEinreihen(
        {
          art: "einreichen",
          pfad: `/inspections/${id}/submit`,
          methode: "POST",
          beschreibung: t("pruefung.nummer", { nummer: pruefung?.number ?? "" }),
          erledigtBei: [409]
        },
        async () => { await api.post(`/inspections/${id}/submit`); }
      );
      if (art === "eingereiht") setHinweis(t("netz.gemerkt"));
      laden();
    } catch (e) {
      const details = e instanceof ApiError
        ? (e.details as { offen?: { punkt: I18nText; grund: string }[] } | undefined)
        : undefined;
      if (details?.offen) setOffen(details.offen);
      setFehler(fehlertext(e, t("fehler.speichern")));
    }
  }

  async function zuruecknehmen() {
    setFehler(null);
    try {
      await api.post(`/inspections/${id}/cancel`);
      laden();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    }
  }

  async function beurteilen(entscheidung: "freigeben" | "zurueck", comment?: string) {
    setFehler(null);
    try {
      await api.post(`/inspections/${id}/review`, { entscheidung, comment });
      /*
       * Nach der Freigabe kommt die Frage nach dem Versand von selbst, statt
       * weiter unten auf der Seite zu warten. Ein freigegebener Bericht, der
       * nur in der Anwendung steht, ist noch nicht zugestellt: der Eigentuemer
       * bekommt ihn als PDF per Mail, und ohne diese Rueckfrage bleibt die
       * Freigabe leicht der letzte Schritt, den jemand tut.
       *
       * Die Freigabe schickt trotzdem nichts. Sie oeffnet nur die Rueckfrage
       * mit den ausgeschriebenen Adressen; der Klick, der das Haus verlaesst,
       * bleibt ein eigener.
       */
      if (entscheidung === "freigeben") setNachFreigabe(true);
      laden();
    } catch (e) {
      setFehler(fehlertext(e));
    }
  }

  /*
   * Punkte nach dem eingefrorenen Bereich buendeln, darin nach der
   * Zwischenueberschrift. Beides steht als Kopie am Bericht: eine spaetere
   * Umbenennung im Baukasten darf einen fertigen Bericht nicht veraendern.
   */
  const gruppen: { name: string; bloecke: { ueberschrift: string; punkte: Punkt[] }[] }[] = [];
  for (const p of punkte) {
    const name = beschriftung(p.module_name_snapshot, i18n.language, "—");
    const ueberschrift = beschriftung(p.gruppe_snapshot, i18n.language);
    let letzte = gruppen.at(-1);
    if (!letzte || letzte.name !== name) {
      letzte = { name, bloecke: [] };
      gruppen.push(letzte);
    }
    const block = letzte.bloecke.at(-1);
    if (block && block.ueberschrift === ueberschrift) block.punkte.push(p);
    else letzte.bloecke.push({ ueberschrift, punkte: [p] });
  }

  const erledigt = punkte.filter((p) => p.result).length;
  /*
   * Gefiltert heisst: dieser Mensch sieht nur die Bereiche seiner Abteilung.
   * Dann zaehlt fuer das Einreichen der ganze Bericht und nicht sein Ausschnitt.
   */
  const gefiltert = gesamt !== null && gesamt.punkte > punkte.length;
  const alleErledigt = gesamt ? gesamt.erledigt >= gesamt.punkte : erledigt >= punkte.length;

  return (
    <>
      <Seitenkopf
        titel={pruefung.customer_name}
        hinweis={`${pruefung.property_name} · ${t("pruefung.nummer", { nummer: pruefung.number })}`}
        aktionen={
          <div className="flex flex-wrap items-center gap-2">
            <StatusMarke status={pruefung.status} />
            <PdfKnopf pruefung={pruefung} onFehler={setFehler} />
            {bearbeitbar && (
              <Knopf onClick={einreichen} disabled={!alleErledigt}>
                {t("pruefung.einreichen")}
              </Knopf>
            )}
            {darfFreigeben && pruefung.status === "submitted" && (
              <>
                <Knopf onClick={() => beurteilen("freigeben")}>{t("pruefung.freigeben")}</Knopf>
                <Knopf art="warnung" onClick={() => {
                  const grund = window.prompt(t("pruefung.rueckgabeGrund") ?? "");
                  if (grund) void beurteilen("zurueck", grund);
                }}>
                  {t("pruefung.zurueckgeben")}
                </Knopf>
              </>
            )}
            {darfFreigeben && !["cancelled", "sent"].includes(pruefung.status) && (
              <Knopf art="neben" onClick={() => void zuruecknehmen()}>
                {t("pruefung.zuruecknehmen")}
              </Knopf>
            )}
            {darfFreigeben && pruefung.status === "cancelled" && (
              <LoeschKnopf
                onLoeschen={async () => {
                  await api.del(`/inspections/${pruefung.id}`);
                  navigate("/pruefungen");
                }}
                onFehler={setFehler}
              />
            )}
          </div>
        }
      />
      <Fehler text={fehler} />
      <AblageHinweis stand={ablageStand} />
      {/*
        * Wer sonst noch an diesem Bericht schreibt.
        *
        * Sie steht oben und nicht am Ende, denn sie aendert, wie man die Liste
        * durchgeht: zu zweit teilt man sie auf, statt sie zweimal zu machen.
        * Faellt derselbe Punkt doch zweimal an, gewinnt der letzte, und ohne
        * diese Zeile wuesste niemand, warum sein Eintrag anders aussieht.
        */}
      {team.length > 1 && (
        <p className="mb-4 rounded border border-marke/40 bg-marke/10 px-3 py-2 text-sm font-semibold">
          {t("pruefung.team", { namen: team.map((m) => m.name).join(", ") })}
        </p>
      )}
      {hinweis && (
        <p className="mb-4 rounded border border-marke bg-marke/10 px-3 py-2 text-sm">{hinweis}</p>
      )}

      {pruefung.status === "changes_requested" && pruefung.review_comment && (
        <Karte className="mb-4 border-s-4 border-red-700">
          <p className="text-sm font-semibold">{t("pruefung.zurueckgegeben")}</p>
          <p className="mt-1 text-sm">{pruefung.review_comment}</p>
        </Karte>
      )}

      {offen.length > 0 && (
        <Karte className="mb-4 border-s-4 border-marke">
          <p className="text-sm font-semibold">{t("pruefung.nochOffen")}</p>
          <ul className="mt-2 space-y-1 text-sm">
            {offen.map((o, i) => (
              <li key={i}>
                {beschriftung(o.punkt, i18n.language)} — {t(`pruefung.grund.${o.grund}`)}
              </li>
            ))}
          </ul>
        </Karte>
      )}

      <p className="mb-4 text-sm text-leise">
        {t("pruefung.fortschritt", { erledigt, gesamt: punkte.length })}
        {gefiltert && gesamt && (
          <>
            {" · "}
            {t("pruefung.fortschrittGesamt", {
              erledigt: gesamt.erledigt, gesamt: gesamt.punkte
            })}
          </>
        )}
      </p>
      {/*
        * Warum hier weniger steht, als der Bericht hat.
        *
        * Ohne diesen Satz sieht ein Bericht mit sieben statt zwanzig Punkten
        * nach einem Fehler aus, und der naechste Griff waere, ihn neu zu
        * starten.
        */}
      {gefiltert && (
        <p className="mb-4 text-sm text-leise">{t("pruefung.nurMeineBereiche")}</p>
      )}

      <div className="space-y-4">
        {gruppen.map((g, i) => (
          <Karte key={i}>
            <h2 className="mb-3 text-lg font-bold">{g.name}</h2>
            {g.bloecke.map((block, bi) => (
            <div key={bi}>
            {block.ueberschrift && (
              <div className="mt-3 text-xs font-semibold uppercase tracking-wide text-leise">
                {block.ueberschrift}
              </div>
            )}
            <ul className="divide-y divide-linie">
              {block.punkte.map((p) => (
                <PunktZeile
                  key={p.id}
                  punkt={p}
                  bearbeitbar={bearbeitbar}
                  onSpeichern={speichern}
                  fotos={fotos.filter((f) => f.item_id === p.id)}
                  pruefungId={id}
                  onFotosGeaendert={laden}
                />
              ))}
            </ul>
            </div>
            ))}
          </Karte>
        ))}
      </div>

      {/*
        Fotos, die zu keinem Punkt gehoeren.
        Der Rundgang, das Wetter, der Zustand der Zufahrt: Bilder, die den
        Bericht erklaeren, ohne dass es dafuer eine Zeile in der Checkliste
        gibt. Ohne diesen Kasten wurden sie an irgendeinen Punkt gehaengt, wo
        sie niemand sucht, und im PDF stand eine Beschriftung, die nicht passt.
        Sie stehen am Ende, weil sie den Rundgang abschliessen und nicht die
        Checkliste unterbrechen sollen.
      */}
      <Karte className="mt-4">
        <h2 className="text-lg font-bold">{t("pruefung.weitereFotos")}</h2>
        <p className="mt-1 text-sm text-leise">{t("pruefung.weitereFotosHinweis")}</p>
        <Fotoleiste
          pruefungId={id}
          itemId={null}
          fotos={fotos.filter((f) => f.item_id === null)}
          bearbeitbar={bearbeitbar}
          pflicht={false}
          onGeaendert={laden}
        />
      </Karte>

      {/*
        * Die Abnahme steht vor der internen Notiz und nach den Punkten.
        *
        * Sie gehoert an das Ende dessen, was der Kunde sieht: er unterschreibt
        * die Liste, nicht die Buerointerna darunter. Sichtbar ist sie, solange
        * der Bericht offen ist oder bereits unterschrieben wurde; einem
        * abgeschlossenen Bericht ohne Unterschrift wuerde eine leere Karte
        * nichts sagen.
        */}
      {(bearbeitbar || pruefung.signed_name) && (
        <Unterschriftsfeld
          pruefungId={pruefung.id}
          vorhandenName={pruefung.signed_name}
          vorhandenMediaId={pruefung.signature_media_id}
          aenderbar={bearbeitbar}
          onGespeichert={laden}
        />
      )}

      <InterneNotiz pruefung={pruefung} onGespeichert={laden} />

      <Kundentext
        pruefung={pruefung}
        onGespeichert={laden}
        verbleibend={verbleibend}
        onVerbraucht={setVerbleibend}
      />

      {darfFreigeben && ["approved", "sent"].includes(pruefung.status) && (
        <VersandKarte
          pruefung={pruefung}
          sofortFragen={nachFreigabe}
          onVersandt={() => { setNachFreigabe(false); laden(); }}
        />
      )}
    </>
  );
}

/**
 * Der interne Kasten am Ende des Berichts.
 *
 * Er steht ueber dem Kundentext und ist rot abgesetzt, damit beim Schreiben
 * kein Zweifel bleibt, welcher der beiden Kaesten das Haus verlaesst. Genau
 * diese Sicherheit ist der Zweck: ein Feld, bei dem man ueberlegen muss, wer
 * es spaeter liest, wird nicht benutzt, und dann steht der Hinweis auf den
 * morschen Zaun am Ende gar nirgends.
 *
 * Immer sichtbar, auch nach dem Versand. Der Kundentext ist dann gesperrt,
 * weil der Kunde ihn gelesen hat; diese Notiz hat er nie gesehen.
 */
function InterneNotiz({ pruefung, onGespeichert }: {
  pruefung: Pruefung;
  onGespeichert: () => void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState(pruefung.internal_note ?? "");
  const [fehler, setFehler] = useState<string | null>(null);
  const [gespeichert, setGespeichert] = useState(false);

  async function speichern() {
    setFehler(null);
    try {
      await api.patch(`/inspections/${pruefung.id}/interne-notiz`, {
        notiz: text.trim() ? text.trim() : null
      });
      setGespeichert(true);
      onGespeichert();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    }
  }

  return (
    <Karte className="mt-6 border-s-4 border-red-700">
      <h2 className="text-lg font-bold">{t("notiz.titel")}</h2>
      <p className="mb-3 mt-1 text-sm text-leise">{t("notiz.hinweis")}</p>
      <Fehler text={fehler} />
      <textarea
        className="min-h-24 w-full rounded border border-linie/40 p-3 text-sm"
        value={text}
        placeholder={t("notiz.platzhalter") ?? ""}
        onChange={(e) => { setText(e.target.value); setGespeichert(false); }}
      />
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Knopf onClick={() => void speichern()}>{t("aktion.speichern")}</Knopf>
        {gespeichert && <span className="text-sm text-leise">{t("pruefung.gespeichert")}</span>}
      </div>
    </Karte>
  );
}

/** Feldnamen der API in Spaltennamen der Anzeige, fuer die sofortige Anzeige. */
function umbenennen(a: Record<string, unknown>): Partial<Punkt> {
  const out: Record<string, unknown> = {};
  if ("result" in a) out.result = a.result;
  if ("valueNumber" in a) out.value_number = a.valueNumber === undefined ? null : String(a.valueNumber);
  if ("valueText" in a) out.value_text = a.valueText ?? null;
  if ("valueBool" in a) out.value_bool = a.valueBool ?? null;
  if ("comment" in a) out.comment = (a.comment as string) ?? null;
  return out as Partial<Punkt>;
}

function PunktZeile({
  punkt, bearbeitbar, onSpeichern, fotos, pruefungId, onFotosGeaendert
}: {
  punkt: Punkt;
  bearbeitbar: boolean;
  onSpeichern: (p: Punkt, a: Record<string, unknown>) => Promise<boolean>;
  fotos: Foto[];
  pruefungId: string;
  onFotosGeaendert: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [wert, setWert] = useState(punkt.value_number ? String(Number(punkt.value_number)) : "");
  const [text, setText] = useState(punkt.value_text ?? "");
  const [kommentar, setKommentar] = useState(punkt.comment ?? "");
  /**
   * Dieser Punkt konnte zuletzt nicht gespeichert werden.
   *
   * Steht direkt am Punkt und nicht oben auf der Seite. Ein Feld, das den Wert
   * weiter anzeigt, obwohl er nie angekommen ist, ist die schlimmste Sorte
   * Fehler: er faellt erst im Buero auf, wenn der Mitarbeiter laengst weg ist.
   */
  const [nichtGespeichert, setNichtGespeichert] = useState(false);

  async function sichern(aenderung: Record<string, unknown>) {
    setNichtGespeichert(!(await onSpeichern(punkt, aenderung)));
  }

  const ziel = zielbereich(punkt.target_min_snapshot, punkt.target_max_snapshot, punkt.unit_snapshot);
  const hinweis = beschriftung(punkt.note_snapshot, i18n.language);
  const kommentarPflicht = punkt.result === "fault" || punkt.is_out_of_range;

  return (
    <li className="py-3">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="font-medium">{beschriftung(punkt.label_snapshot, i18n.language)}</span>
        {ziel && <span className="rounded bg-grund px-2 py-0.5 text-xs">{ziel}</span>}
        {punkt.is_out_of_range && (
          <span className="rounded bg-red-100 px-2 py-0.5 text-xs text-red-800">{t("pruefung.ausserhalb")}</span>
        )}
      </div>
      {hinweis && <p className="mt-1 text-xs text-leise">{hinweis}</p>}

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {punkt.input_type_snapshot === "number" && (
          <Eingabe
            inputMode="decimal"
            className="w-32 rounded border border-linie/40 bg-white px-3 py-2 outline-none focus:border-marke"
            value={wert}
            disabled={!bearbeitbar}
            onChange={(e) => setWert(e.target.value)}
            onBlur={() => void sichern({ valueNumber: wert, result: punkt.result ?? "ok" })}
            aria-label={t("pruefung.wert")}
          />
        )}
        {punkt.input_type_snapshot === "text" && (
          <Textfeld
            rows={2}
            value={text}
            disabled={!bearbeitbar}
            onChange={(e) => setText(e.target.value)}
            onBlur={() => void sichern({ valueText: text, result: punkt.result ?? "done" })}
            aria-label={t("pruefung.wert")}
          />
        )}
        {punkt.input_type_snapshot === "boolean" ? (
          <ErgebnisKnoepfe
            werte={["done", "na"]}
            aktuell={punkt.result}
            bearbeitbar={bearbeitbar}
            beschriftung={(w) => (w === "done" ? t("pruefung.ja") : t("pruefung.nein"))}
            onWahl={(w) => void sichern({ result: w, valueBool: w === "done" })}
          />
        ) : (
          /*
            Vier Moeglichkeiten, weil eine Liste aus zwei Sorten Punkten
            besteht: Aufgaben werden erledigt, Kontrollen sind in Ordnung oder
            nicht. "Nicht kontrolliert" ist die ehrliche vierte Antwort und
            besser als ein Punkt, der leer bleibt und im Bericht fehlt.
          */
          <ErgebnisKnoepfe
            werte={["done", "ok", "fault", "na"]}
            aktuell={punkt.result}
            bearbeitbar={bearbeitbar}
            beschriftung={(w) => t(`pruefung.ergebnis.${w}`)}
            onWahl={(w) => void sichern({ result: w })}
          />
        )}
      </div>

      {nichtGespeichert && (
        <p className="mt-2 rounded border border-red-700 bg-red-50 px-2 py-1 text-xs font-semibold text-red-800">
          {t("pruefung.nichtGespeichert")}
        </p>
      )}

      {(kommentarPflicht || kommentar) && (
        <div className="mt-2">
          <Eingabe
            value={kommentar}
            disabled={!bearbeitbar}
            placeholder={kommentarPflicht ? t("pruefung.kommentarPflicht") : t("pruefung.kommentar")}
            onChange={(e) => setKommentar(e.target.value)}
            onBlur={() => void sichern({ comment: kommentar, commentLang: i18n.language })}
          />
          {kommentarPflicht && !kommentar.trim() && (
            <p className="mt-1 text-xs text-red-700">{t("pruefung.kommentarFehlt")}</p>
          )}
        </div>
      )}

      {punkt.comment && punkt.comment_lang && !["de", "en"].includes(punkt.comment_lang) && (
        <Uebersetzung pruefungId={pruefungId} itemId={punkt.id} />
      )}

      {/*
        Aus einem Mangel wird ein Auftrag, direkt hier.
        Der Moment, in dem jemand den Mangel sieht, ist der einzige, in dem er
        sicher noch weiss, worum es geht. Wer das auf spaeter verschiebt,
        schreibt den Auftrag entweder gar nicht oder ungenau.
      */}
      {punkt.result === "fault" && <AuftragAusMangel pruefungId={pruefungId} itemId={punkt.id} />}

      <Fotoleiste
        pruefungId={pruefungId}
        itemId={punkt.id}
        fotos={fotos}
        bearbeitbar={bearbeitbar}
        // Pflicht, sobald etwas erfasst ist. "Entfaellt" ist ausgenommen: was es
        // am Objekt nicht gibt, laesst sich nicht fotografieren.
        pflicht={
          punkt.photo_required_snapshot === true &&
          punkt.result !== null &&
          punkt.result !== "na"
        }
        onGeaendert={onFotosGeaendert}
      />
    </li>
  );
}

/**
 * Eine Notiz uebersetzen lassen.
 *
 * Das Original bleibt stehen, die Uebersetzung kommt darunter. Der Kommentar
 * des Mitarbeiters ist der Beleg, und einen Beleg ersetzt man nicht durch eine
 * Maschinenfassung. Wer die Uebersetzung im Kundentext haben will, uebernimmt
 * sie bewusst dorthin.
 */
function Uebersetzung({ pruefungId, itemId }: { pruefungId: string; itemId: string }) {
  const { t, i18n } = useTranslation();
  const [text, setText] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  async function holen() {
    setLaeuft(true);
    setFehler(null);
    try {
      const ziel = i18n.language === "en" ? "en" : "de";
      const r = await api.post<{ uebersetzung: string }>(
        `/inspections/${pruefungId}/items/${itemId}/translate`, { sprache: ziel }
      );
      setText(r.uebersetzung);
    } catch (e) {
      setFehler(fehlertext(e, t("ki.fehler")));
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <div className="mt-1">
      {text ? (
        <p className="text-xs text-leise">
          <span className="font-semibold">{t("ki.marke")}: </span>{text}
        </p>
      ) : (
        <button className="text-xs text-leise underline" onClick={() => void holen()} disabled={laeuft}>
          {laeuft ? t("ki.denkt") : t("ki.uebersetzen")}
        </button>
      )}
      {fehler && <p className="mt-1 text-xs text-red-700">{fehler}</p>}
    </div>
  );
}

/**
 * Fotos, die noch auf dem Geraet liegen.
 *
 * Ohne diese Anzeige waere die haeufigste Frage am Objekt unbeantwortet: "ist
 * mein Bild jetzt drin?" Der Mitarbeiter saehe nach dem Fotografieren ohne
 * Netz nichts und fotografierte ein zweites und drittes Mal.
 *
 * Die Bilder kommen aus der Warteschlange selbst, nicht vom Server. Sobald
 * eines uebertragen ist, verschwindet es hier und taucht oben bei den
 * uebrigen auf.
 */
function WartendeBilder({ itemId }: { itemId: string | null }) {
  const { t } = useTranslation();
  const [bilder, setBilder] = useState<{ id: string; url: string }[]>([]);

  useEffect(() => {
    let aktiv = true;
    /*
     * Je Eintrag genau ein Objektverweis, gemerkt an seiner Kennung. Ohne
     * dieses Gedaechtnis entstuende bei jeder Meldung der Warteschlange ein
     * neuer Verweis auf dasselbe Bild, und der Speicher liefe voll.
     */
    const verweise = new Map<string, string>();

    const nachsehen = () => {
      void eintraege().then((liste) => {
        if (!aktiv) return;
        const meine = liste.filter(
          (e) => e.art === "foto" && (e.datei?.felder?.itemId ?? "") === (itemId ?? "")
        );
        for (const e of meine) {
          if (!verweise.has(e.id)) verweise.set(e.id, URL.createObjectURL(e.datei!.blob));
        }
        for (const [id, url] of verweise) {
          if (!meine.some((e) => e.id === id)) {
            URL.revokeObjectURL(url);
            verweise.delete(id);
          }
        }
        setBilder(meine.map((e) => ({ id: e.id, url: verweise.get(e.id)! })));
      });
    };

    const abmelden = beobachten(nachsehen);
    return () => {
      aktiv = false;
      abmelden();
      for (const url of verweise.values()) URL.revokeObjectURL(url);
    };
  }, [itemId]);

  if (bilder.length === 0) return null;
  return (
    <div className="mt-2">
      <p className="text-xs text-leise">{t("netz.gemerkt")}</p>
      <div className="mt-1 flex flex-wrap gap-2">
        {bilder.map((b) => (
          <div key={b.id} className="h-20 w-20 overflow-hidden rounded border-2 border-dashed border-marke">
            <img src={b.url} alt="" className="h-full w-full object-cover" />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Fotos zu einem Pruefpunkt.
 *
 * **Zwei Knoepfe, weil es zwei Wege gibt.** "Fotografieren" oeffnet am Telefon
 * unmittelbar die Rueckkamera: wer am Objekt steht, fotografiert den Schaden
 * und sucht ihn nicht erst in seinen Bildern. "Aus der Galerie" nimmt, was
 * schon aufgenommen wurde, etwa weil der Mitarbeiter beim Rundgang
 * fotografiert hat und den Bericht erst im Auto schreibt.
 *
 * **Mehrere auf einmal.** Beide Felder nehmen mehrere Dateien, und der Upload
 * blockiert die Knoepfe nicht mehr. Vorher war jedes Bild ein Vorgang: Foto,
 * warten, Foto, warten. Bei einem Pool mit vier Schaeden ist das eine Minute
 * Stehen, und am Ende fotografiert niemand mehr den vierten.
 *
 * Die Bilder gehen einzeln raus, nicht als Paket. Faellt das Netz nach dem
 * zweiten von fuenf aus, sind zwei da und drei warten in der Warteschlange;
 * bei einem Paket waeren alle fuenf weg.
 */
function Fotoleiste({
  pruefungId, itemId, fotos, bearbeitbar, pflicht, onGeaendert
}: {
  pruefungId: string;
  /** Der Pruefpunkt, zu dem das Bild gehoert. Null heisst: zum Bericht selbst. */
  itemId: string | null;
  fotos: Foto[];
  bearbeitbar: boolean;
  pflicht: boolean;
  onGeaendert: () => void;
}) {
  const { t } = useTranslation();
  const kamera = useRef<HTMLInputElement>(null);
  const galerie = useRef<HTMLInputElement>(null);
  /** Wie viele Bilder gerade unterwegs sind. Null heisst: nichts laeuft. */
  const [offen, setOffen] = useState(0);
  const [fehler, setFehler] = useState<string | null>(null);
  /** Ohne Netz aufgenommen: das Bild liegt auf dem Geraet und wartet. */
  const [gemerkt, setGemerkt] = useState(false);

  /**
   * Eine Auswahl aus einem der beiden Felder.
   *
   * Nacheinander und nicht gleichzeitig: fuenf Bilder parallel ueber eine
   * Mobilfunkverbindung zu schieben macht alle fuenf langsam und bringt die
   * Warteschlange durcheinander. Der Knopf bleibt trotzdem bedienbar, damit
   * das naechste Foto nicht auf das vorherige wartet.
   */
  async function auswahl(dateien: FileList | null) {
    const liste = Array.from(dateien ?? []);
    if (liste.length === 0) return;
    setOffen((n) => n + liste.length);
    setFehler(null);
    setGemerkt(false);
    for (const datei of liste) {
      await hochladen(datei);
      setOffen((n) => Math.max(0, n - 1));
    }
  }

  async function hochladen(datei: File) {
    try {
      /*
       * Das Foto ist der Fall, fuer den die Warteschlange gebaut wurde. Ein
       * Bild vom Objekt laesst sich nicht nachtraeglich aufnehmen: wer es
       * wegen einer Funkluecke verliert, muss ein zweites Mal hinfahren.
       * Deshalb wandert es mitsamt seinen Daten auf das Geraet und geht
       * spaeter von selbst raus.
       */
      const art = await sendenOderEinreihen(
        {
          art: "foto",
          pfad: `/inspections/${pruefungId}/photos`,
          methode: "POST",
          /*
           * Leer statt fehlend, weil die Warteschlange ihre Felder als Text
           * ablegt. Der Server macht daraus wieder null, und das Bild haengt
           * dann am Bericht statt an einem Punkt.
           */
          datei: { blob: datei, name: datei.name || "foto.jpg", felder: { itemId: itemId ?? "" } },
          beschreibung: t("foto.aufnehmen")
        },
        async () => {
          const form = new FormData();
          if (itemId) form.append("itemId", itemId);
          form.append("datei", datei);
          await dateiSenden(`/inspections/${pruefungId}/photos`, form);
          onGeaendert();
        }
      );
      if (art === "eingereiht") setGemerkt(true);
    } catch (e) {
      setFehler(fehlertext(e, t("foto.fehler")));
    } finally {
      // Dasselbe Bild zweimal auszuwaehlen loeste ohne das Leeren kein
      // change-Ereignis aus, und der zweite Versuch tat nichts.
      if (kamera.current) kamera.current.value = "";
      if (galerie.current) galerie.current.value = "";
    }
  }

  async function entfernen(id: string) {
    setFehler(null);
    try {
      await api.del(`/inspection-media/${id}`);
      onGeaendert();
    } catch (e) {
      setFehler(fehlertext(e, t("foto.fehler")));
    }
  }

  if (!bearbeitbar && fotos.length === 0) return null;

  return (
    <div className="mt-2">
      {fotos.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {fotos.map((f) => (
            <Vorschaubild
              key={f.id}
              mediaId={f.thumb_id ?? f.media_id}
              beschriftung={f.caption}
              onEntfernen={bearbeitbar ? () => void entfernen(f.id) : undefined}
            />
          ))}
        </div>
      )}

      <WartendeBilder itemId={itemId} />

      {bearbeitbar && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input
            ref={kamera}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            className="hidden"
            onChange={(e) => void auswahl(e.target.files)}
          />
          {/* Ohne capture bietet das Telefon die Galerie an, mit multiple
              lassen sich mehrere Bilder auf einmal auswaehlen. */}
          <input
            ref={galerie}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => void auswahl(e.target.files)}
          />
          <Knopf art="neben" onClick={() => kamera.current?.click()}>
            {t("foto.aufnehmen")}
          </Knopf>
          <Knopf art="neben" onClick={() => galerie.current?.click()}>
            {t("foto.galerie")}
          </Knopf>
          {offen > 0 && (
            <span className="text-xs text-leise">
              {t("foto.laedtAnzahl", { count: offen })}
            </span>
          )}
          {gemerkt && <span className="text-xs text-leise">{t("netz.gemerkt")}</span>}
          {pflicht && fotos.length === 0 && (
            <span className="text-xs text-red-700">{t("foto.pflicht")}</span>
          )}
        </div>
      )}
      {fehler && <p className="mt-1 text-xs text-red-700">{fehler}</p>}
    </div>
  );
}

/**
 * Ein einzelnes Bild. Holt sich die Daten selbst, weil ein <img src> das
 * Zugriffstoken nicht mitschickt, und gibt den Verweis beim Aufraeumen wieder
 * frei. Ohne das Freigeben wachsen bei einem Rundgang mit dreissig Bildern
 * einige Megabyte im Speicher an, die niemand mehr braucht.
 */
function Vorschaubild({
  mediaId, beschriftung: text, onEntfernen
}: {
  mediaId: string;
  beschriftung: string | null;
  onEntfernen?: () => void;
}) {
  const { t } = useTranslation();
  const [quelle, setQuelle] = useState<string | null>(null);

  useEffect(() => {
    let verworfen = false;
    let verweis: string | null = null;
    void bildLaden(`/media/${mediaId}`)
      .then((url) => {
        if (verworfen) { URL.revokeObjectURL(url); return; }
        verweis = url;
        setQuelle(url);
      })
      .catch(() => undefined);
    return () => {
      verworfen = true;
      if (verweis) URL.revokeObjectURL(verweis);
    };
  }, [mediaId]);

  return (
    <figure className="relative">
      <div className="h-20 w-20 overflow-hidden rounded bg-grund">
        {quelle && <img src={quelle} alt={text ?? ""} className="h-full w-full object-cover" />}
      </div>
      {onEntfernen && (
        <button
          type="button"
          onClick={onEntfernen}
          aria-label={t("foto.entfernen")}
          className="absolute -end-1 -top-1 rounded-full bg-tinte px-1.5 text-xs font-bold text-white"
        >
          x
        </button>
      )}
    </figure>
  );
}

function ErgebnisKnoepfe({
  werte, aktuell, bearbeitbar, beschriftung: text, onWahl
}: {
  werte: readonly string[];
  aktuell: string | null;
  bearbeitbar: boolean;
  beschriftung: (w: string) => string;
  onWahl: (w: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {werte.map((w) => {
        const gewaehlt = aktuell === w;
        const farbe = !gewaehlt
          ? "border border-linie/40 bg-white text-leise"
          : w === "fault" ? "bg-red-700 text-white"
          : w === "na" ? "bg-leise text-white"
          : "bg-green-700 text-white";
        return (
          <button
            key={w}
            type="button"
            disabled={!bearbeitbar}
            onClick={() => onWahl(w)}
            className={`min-h-11 rounded px-3 py-2 text-sm font-semibold transition disabled:opacity-60 ${farbe}`}
          >
            {text(w)}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Endgueltig loeschen, in zwei Schritten.
 *
 * Kein window.confirm: der Dialog des Browsers laesst sich wegklicken, ohne
 * gelesen zu werden, und auf dem Telefon steht er an einer Stelle, an der der
 * Daumen ohnehin gerade liegt. Zwei getrennte Schaltflaechen mit einer klaren
 * Frage dazwischen sind fuer eine unumkehrbare Handlung die ehrlichere Loesung.
 */
function LoeschKnopf({
  onLoeschen, onFehler
}: {
  onLoeschen: () => Promise<void>;
  onFehler: (s: string | null) => void;
}) {
  const { t } = useTranslation();
  const [gefragt, setGefragt] = useState(false);
  const [laeuft, setLaeuft] = useState(false);

  if (!gefragt) {
    return (
      <Knopf art="warnung" onClick={() => { onFehler(null); setGefragt(true); }}>
        {t("pruefung.loeschen")}
      </Knopf>
    );
  }

  return (
    <span className="flex flex-wrap items-center gap-2">
      <span className="text-sm font-semibold">{t("pruefung.loeschenFrage")}</span>
      <Knopf
        art="warnung"
        disabled={laeuft}
        onClick={async () => {
          setLaeuft(true);
          try {
            await onLoeschen();
          } catch (e) {
            onFehler(fehlertext(e, t("pruefung.loeschenFehler")));
            setLaeuft(false);
            setGefragt(false);
          }
        }}
      >
        {laeuft ? t("aktion.speichert") : t("pruefung.loeschenJa")}
      </Knopf>
      <Knopf art="neben" onClick={() => setGefragt(false)}>{t("aktion.abbrechen")}</Knopf>
    </span>
  );
}

/**
 * Der Bericht als PDF.
 *
 * Voreingestellt ist die Sprache des Eigentuemers, denn in dieser Sprache geht
 * der Bericht hinaus. Die zweite Schaltflaeche gibt es fuer den Fall, dass im
 * Buero jemand mitlesen muss, der die andere Sprache spricht. Ein noch nicht
 * freigegebener Bericht traegt im PDF sichtbar den Vermerk ENTWURF.
 */
function PdfKnopf({ pruefung, onFehler }: { pruefung: Pruefung; onFehler: (s: string | null) => void }) {
  const { t } = useTranslation();
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const kundensprache = pruefung.customer_language === "en" ? "en" : "de";
  const andere = kundensprache === "de" ? "en" : "de";

  async function laden(sprache: string) {
    setLaeuft(sprache);
    onFehler(null);
    try {
      await dateiLaden(
        `/inspections/${pruefung.id}/pdf?sprache=${sprache}`,
        /*
         * Nur der Ersatzname. Wie die Datei wirklich heisst, sagt der Server
         * im Kopf der Antwort: dort steht der Betrieb aus den Firmenangaben
         * davor, und der ist hier nicht bekannt.
         */
        `Bericht-${pruefung.number}.pdf`
      );
    } catch (e) {
      onFehler(fehlertext(e, t("pruefung.pdfFehler")));
    } finally {
      setLaeuft(null);
    }
  }

  return (
    <>
      <Knopf art="neben" onClick={() => void laden(kundensprache)} disabled={laeuft !== null}>
        {t("pruefung.pdf")} ({t(`sprache.${kundensprache}`)})
      </Knopf>
      <Knopf art="neben" onClick={() => void laden(andere)} disabled={laeuft !== null}>
        {t(`sprache.${andere}`)}
      </Knopf>
    </>
  );
}

function Kundentext({
  pruefung,
  onGespeichert,
  verbleibend,
  onVerbraucht
}: {
  pruefung: Pruefung;
  onGespeichert: () => void;
  verbleibend: number;
  onVerbraucht: (n: number) => void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState(pruefung.summary_text ?? "");
  const [sprache, setSprache] = useState(pruefung.summary_lang ?? pruefung.customer_language ?? "de");
  const [fehler, setFehler] = useState<string | null>(null);
  const [gespeichert, setGespeichert] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  /*
   * Woher der Text stammt, wird mitgefuehrt und mitgespeichert.
   *
   * "ai" heisst unveraendert von Claude, "ai_edited" heisst von Claude und
   * danach angefasst, "manual" heisst selbst geschrieben. Ohne diese
   * Unterscheidung liesse sich spaeter nicht mehr sagen, welche Kundentexte je
   * ein Mensch gelesen hat, und genau das ist die Frage, die im Zweifel zaehlt.
   */
  const [quelle, setQuelle] = useState<"manual" | "ai" | "ai_edited">("manual");

  if (["cancelled", "sent"].includes(pruefung.status)) return null;

  async function vorschlagen() {
    setLaeuft(true);
    setFehler(null);
    try {
      const r = await api.post<{ vorschlag: string; verbleibend?: number }>(
        `/inspections/${pruefung.id}/summary/suggest`, { sprache }
      );
      setText(r.vorschlag);
      setQuelle("ai");
      setGespeichert(false);
      if (typeof r.verbleibend === "number") onVerbraucht(r.verbleibend);
    } catch (e) {
      setFehler(fehlertext(e, t("ki.fehler")));
    } finally {
      setLaeuft(false);
    }
  }

  async function speichern() {
    setFehler(null);
    try {
      await api.patch(`/inspections/${pruefung.id}/summary`, {
        summaryLang: sprache, summaryText: text, summarySource: quelle
      });
      setGespeichert(true);
      onGespeichert();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    }
  }

  return (
    <Karte className="mt-6">
      <h2 className="mb-1 font-bold">{t("pruefung.kundentext")}</h2>
      <p className="mb-3 text-sm text-leise">{t("pruefung.kundentextHinweis")}</p>
      <Fehler text={fehler} />
      <div className="mb-3 max-w-32">
        <Auswahl value={sprache} onChange={(e) => setSprache(e.target.value)} aria-label={t("kunden.sprache")}>
          <option value="de">DE</option>
          <option value="en">EN</option>
        </Auswahl>
      </div>
      <Textfeld
        rows={5}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setGespeichert(false);
          // Ab der ersten Aenderung ist es nicht mehr Claudes Text allein.
          setQuelle((q) => (q === "ai" ? "ai_edited" : q));
        }}
      />
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Knopf onClick={speichern}>{t("aktion.speichern")}</Knopf>
        {/*
          * Zwei Anlaeufe je Bericht. Die Zahl steht neben dem Knopf und nicht
          * darin, damit die Beschriftung in allen vier Sprachen gleich breit
          * bleibt und der Knopf beim Zaehlen nicht springt.
          */}
        <Knopf art="neben" onClick={() => void vorschlagen()} disabled={laeuft || verbleibend <= 0}>
          {laeuft ? t("ki.denkt") : t("ki.vorschlag")}
        </Knopf>
        <span className="text-xs text-leise">
          {verbleibend > 0 ? t("ki.rest", { n: verbleibend }) : t("ki.aufgebraucht")}
        </span>
        {quelle !== "manual" && (
          <span className="rounded bg-grund px-2 py-0.5 text-xs">
            {quelle === "ai" ? t("ki.marke") : t("ki.markeBearbeitet")}
          </span>
        )}
        {gespeichert && <span className="text-sm text-leise">{t("pruefung.gespeichert")}</span>}
      </div>
      <p className="mt-2 text-xs text-leise">{t("ki.hinweis")}</p>
    </Karte>
  );
}

interface Versandstand {
  moeglich: boolean;
  grund: string | null;
  eingerichtet: boolean;
  an: string[];
  blindkopie: string[];
  sprache: string;
  betreff: string;
  bereitsGesendet: { am: string; an: string } | null;
  letzterVersuch: { am: string; status: string; fehler: string | null } | null;
}

/**
 * Der Versand an den Eigentuemer.
 *
 * Bewusst ein eigener Schritt nach der Freigabe, und bewusst mit Rueckfrage.
 * Der Klick verlaesst das Haus: die Empfaenger stehen deshalb ausgeschrieben
 * da, bevor er moeglich ist. Ein Knopf, der ungefragt an eine Adresse
 * schickt, die niemand gesehen hat, ist an dieser Stelle das falsche Werkzeug.
 */
function VersandKarte({ pruefung, sofortFragen, onVersandt }: {
  pruefung: Pruefung;
  /** Gerade freigegeben: dann steht die Rueckfrage sofort offen. */
  sofortFragen?: boolean;
  onVersandt: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [stand, setStand] = useState<Versandstand | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [gefragt, setGefragt] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const karte = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void api.get<Versandstand>(`/inspections/${pruefung.id}/versand`)
      .then(setStand)
      .catch((e) => setFehler(fehlertext(e, t("fehler.laden"))));
  }, [pruefung.id, pruefung.status]);

  /*
   * Nach der Freigabe die Frage aufschlagen und hinscrollen.
   *
   * Auch dann, wenn der Versand nicht moeglich ist: dann steht dort der Grund,
   * etwa dass beim Kunden keine Adresse hinterlegt ist. Das ist genau der
   * Augenblick, in dem jemand sie nachtraegt, und nicht drei Wochen spaeter,
   * wenn der Eigentuemer anruft.
   */
  useEffect(() => {
    if (!sofortFragen || !stand) return;
    if (stand.moeglich) setGefragt(true);
    karte.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [sofortFragen, stand]);

  async function senden() {
    setLaeuft(true);
    setFehler(null);
    try {
      await api.post(`/inspections/${pruefung.id}/send`);
      setGefragt(false);
      onVersandt();
    } catch (e) {
      setFehler(fehlertext(e, t("versand.fehler")));
    } finally {
      setLaeuft(false);
    }
  }

  const gesendet = stand?.bereitsGesendet ?? null;

  return (
    <Karte className={`mt-6 ${sofortFragen ? "border-2 border-marke" : ""}`}>
      <div ref={karte} />
      <h2 className="mb-1 font-bold">{t("versand.titel")}</h2>
      <p className="mb-3 text-sm text-leise">{t("versand.hinweis")}</p>
      <Fehler text={fehler} />

      {!stand ? (
        <Laedt />
      ) : (
        <>
          <dl className="mb-3 grid gap-1 text-sm sm:grid-cols-[8rem_1fr]">
            <dt className="text-leise">{t("versand.an")}</dt>
            <dd>{stand.an.length ? stand.an.join(", ") : "—"}</dd>
            {stand.blindkopie.length > 0 && (
              <>
                <dt className="text-leise">{t("versand.kopie")}</dt>
                <dd>{stand.blindkopie.join(", ")}</dd>
              </>
            )}
            <dt className="text-leise">{t("versand.betreff")}</dt>
            <dd>{stand.betreff}</dd>
            <dt className="text-leise">{t("versand.anhang")}</dt>
            <dd>{t("versand.anhangPdf", { sprache: t(`sprache.${stand.sprache}`) })}</dd>
          </dl>

          {gesendet && (
            <p className="mb-3 rounded bg-grund px-3 py-2 text-sm">
              {t("versand.bereits", {
                datum: new Date(gesendet.am).toLocaleString(i18n.language),
                an: gesendet.an
              })}
            </p>
          )}

          {/*
            * Die Antwort des Mailservers im Klartext.
            *
            * Beim Einrichten ist dieser Satz die ganze Information. "Nicht
            * angenommen" allein zwingt sonst dazu, in einer Tabelle
            * nachzusehen, an die niemand ohne Datenbankzugang kommt.
            */}
          {stand.letzterVersuch?.status === "error" && (
            <p className="mb-3 break-words rounded bg-grund px-3 py-2 text-xs text-leise">
              {t("versand.letzterFehler", {
                datum: new Date(stand.letzterVersuch.am).toLocaleString(i18n.language),
                fehler: stand.letzterVersuch.fehler ?? ""
              })}
            </p>
          )}

          {!stand.moeglich ? (
            <p className="text-sm text-leise">{stand.grund}</p>
          ) : !gefragt ? (
            <Knopf onClick={() => { setFehler(null); setGefragt(true); }}>
              {gesendet ? t("versand.erneut") : t("versand.senden")}
            </Knopf>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold">
                {t("versand.frage", { an: stand.an.join(", ") })}
              </span>
              <Knopf disabled={laeuft} onClick={() => void senden()}>
                {laeuft ? t("versand.laeuft") : t("versand.ja")}
              </Knopf>
              <Knopf art="neben" onClick={() => setGefragt(false)}>{t("aktion.abbrechen")}</Knopf>
            </div>
          )}
        </>
      )}
    </Karte>
  );
}

/** Die Pruefungen eines Objekts, eingebunden auf der Objektseite. */
export function PruefungenAmObjekt({ propertyId }: { propertyId: string }) {
  const { t, i18n } = useTranslation();
  const [liste, setListe] = useState<PruefungZeile[] | null>(null);

  useEffect(() => {
    void api.get<{ inspections: PruefungZeile[] }>(`/inspections?propertyId=${propertyId}`)
      .then((r) => setListe(r.inspections)).catch(() => setListe([]));
  }, [propertyId]);

  return (
    <Karte className="lg:col-span-2">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-bold">{t("pruefung.titel")}</h2>
        <Link to={`/pruefungen/neu?objekt=${propertyId}`}>
          <Knopf art="neben">{t("pruefung.neu")}</Knopf>
        </Link>
      </div>
      {!liste ? <Laedt /> : liste.length === 0 ? (
        <p className="text-sm text-leise">{t("pruefung.leerBeiObjekt")}</p>
      ) : (
        <ul className="space-y-2">
          {liste.slice(0, 10).map((p) => (
            <li key={p.id}>
              <Link to={`/pruefungen/${p.id}`} className="flex flex-wrap items-center gap-2 rounded px-2 py-2 hover:bg-grund">
                <span className="font-semibold">{t("pruefung.nummer", { nummer: p.number })}</span>
                <StatusMarke status={p.status} />
                <span className="text-sm text-leise">
                  {p.employee_name}
                  {p.started_at ? ` · ${new Date(p.started_at).toLocaleDateString(i18n.language)}` : ""}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Karte>
  );
}

/**
 * Der Weg vom Mangel zum Auftrag.
 *
 * Bewusst ohne Formular: Titel und Beschreibung baut die API aus Pruefpunkt und
 * Kommentar. Wer mehr braucht, findet den Auftrag danach offen und kann ihn
 * ergaenzen. Ein Formular an dieser Stelle wuerde die Erfassung unterbrechen.
 */
function AuftragAusMangel({ pruefungId, itemId }: { pruefungId: string; itemId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  async function anlegen() {
    setLaeuft(true);
    setFehler(null);
    try {
      const a = await api.post<{ id: string }>(
        `/inspections/${pruefungId}/items/${itemId}/work-order`
      );
      navigate(`/auftraege/${a.id}`);
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
      setLaeuft(false);
    }
  }

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => void anlegen()}
        disabled={laeuft}
        className="text-sm font-semibold text-verweis underline disabled:opacity-60"
      >
        {t("auftraege.ausMangel")}
      </button>
      {fehler && <p className="mt-1 text-xs text-red-700">{fehler}</p>}
    </div>
  );
}
